import React from 'react';
import { ApolloError } from '@apollo/client';
import { useFetcher, useLoaderData, useRevalidator, useSearchParams } from 'react-router-dom';
import {
  ClockIcon,
  EllipsisVerticalIcon,
  EnvelopeIcon,
  PencilSquareIcon,
  PowerIcon,
  StarIcon,
  UserGroupIcon,
  UserIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';

import { getApiBaseUrl } from '@oysterjs/core/config';
import { type ActionResult } from '@oysterjs/core/graphql/actions';
import { messageFromError } from '@oysterjs/core/graphql/errors';
import { Await } from '@oysterjs/ui/async';
import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '@oysterjs/ui/dropdown';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label
} from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { Spinner } from '@oysterjs/ui/spinner';
import { Switch, SwitchField } from '@oysterjs/ui/switch';

import { client } from '~/api/client';
import {
  DeleteMailboxSignatureDocument,
  type DeleteMailboxSignatureMutation,
  type DeleteMailboxSignatureMutationVariables,
  useMailboxAccountMonitoringQuery,
  type MailboxMonitoringFieldsFragment,
  MailboxMonitoringProblemKind,
  MailboxMonitoringNoteCode,
  MailboxAccountSharingDocument,
  type MailboxAccountSharingQuery,
  type MailboxAccountSharingQueryVariables,
  type MailboxAccountFieldsFragment,
  MailboxAccountStatus,
  MailboxProvider,
  type MailboxSharingFieldsFragment,
  type MailboxSignatureSettingsFieldsFragment,
  MailboxSignaturesForSettingsDocument,
  type MailboxSignaturesForSettingsQuery,
  type MailboxSignaturesForSettingsQueryVariables,
  SetDefaultMailboxDocument,
  type SetDefaultMailboxMutation,
  type SetDefaultMailboxMutationVariables,
  SetMailboxVisibilityDocument,
  type SetMailboxVisibilityMutation,
  type SetMailboxVisibilityMutationVariables,
  UpsertMailboxSignatureDocument,
  type UpsertMailboxSignatureMutation,
  type UpsertMailboxSignatureMutationVariables,
  useGetAgencyUsersQuery,
  useGetTeamsQuery,
  Visibility
} from '~/api/graphql';
import { useOptionalCurrentAgencyUser } from '~/components/current-user';
import { SignaturePreview } from '~/components/signature-preview';
import { useVisibilityEntitlement } from '~/components/visibility/use-visibility-entitlement';
import { VisibilitySelector } from '~/components/visibility/visibility-selector';
import {
  visibilityInputFromValue,
  visibilityValueFromFragment,
  type VisibilitySelectorValue
} from '~/components/visibility/visibility-selector.utils';
import {
  mailboxProviderList,
  mailboxProviderSlug,
  mailboxProviders
} from '~/lib/mailbox-providers';
import { HtmlDocumentEditor } from '~/pages/copilot/workspace/legacy-editor/html-document-editor';
import { MailboxEventsDrawer } from './mailbox-events';

const mailboxSettingsPath = '/app/settings/mailbox';

/**
 * The client runs with `errorPolicy: 'all'`, so a refusal arrives as a resolved
 * result carrying `errors` rather than as a rejection.
 */
const dataOrThrow = <T,>(result: {
  data?: T | null;
  errors?: ApolloError['graphQLErrors'];
}): T | null | undefined => {
  if (result.errors?.length) {
    throw new ApolloError({ graphQLErrors: result.errors });
  }
  return result.data;
};

// The provider's consent screen is a full navigation, so this is an href, not a
// fetch. `accountId` repairs an existing connection rather than adding one.
export const mailboxConnectUrl = (provider: MailboxProvider, accountId?: string) => {
  const params = new URLSearchParams({
    provider: mailboxProviderSlug(provider),
    returnPath: mailboxSettingsPath
  });
  if (accountId) {
    params.set('accountId', accountId);
  }
  return `${getApiBaseUrl()}/mailbox/oauth/start?${params.toString()}`;
};

type MailboxLoaderData = {
  data: Promise<{
    mailboxAccounts: MailboxAccountFieldsFragment[];
    mailboxProviders: MailboxProvider[];
  }>;
};

export const SettingsMailboxPage = () => {
  const loader = useLoaderData() as MailboxLoaderData;
  const currentUserId = useOptionalCurrentAgencyUser()?.id;

  return (
    <React.Suspense fallback={<MailboxPageLoader />}>
      <Await resolve={loader.data}>
        {(data) => (
          <div className="space-y-6">
            <MailboxOutcomeBanner />
            <ConnectMailboxCard connectable={data?.mailboxProviders ?? []} />
            <MailboxAccountsCard
              key={currentUserId ?? 'unknown'}
              accounts={data?.mailboxAccounts ?? []}
            />
            <MailboxSignaturesCard />
          </div>
        )}
      </Await>
    </React.Suspense>
  );
};

/** A mailbox tile paired with the sharing record the settings page read for it. */
type MailboxSharingTarget = {
  account: MailboxAccountFieldsFragment;
  sharing: MailboxSharingFieldsFragment;
};

/** The sharing record is absent when the confirmation opens before the sharing
 *  read lands. */
type MailboxDisconnectTarget = {
  account: MailboxAccountFieldsFragment;
  sharing?: MailboxSharingFieldsFragment;
};

const MailboxAccountsCard = ({ accounts }: { accounts: MailboxAccountFieldsFragment[] }) => {
  const revalidator = useRevalidator();
  const currentUserId = useOptionalCurrentAgencyUser()?.id;
  const [disconnecting, setDisconnecting] = React.useState<MailboxDisconnectTarget | null>(null);
  const [managing, setManaging] = React.useState<MailboxSharingTarget | null>(null);
  const [eventsTarget, setEventsTarget] = React.useState<MailboxAccountFieldsFragment | null>(null);
  const [sharing, setSharing] = React.useState<Record<string, MailboxSharingFieldsFragment>>({});
  const [sharingError, setSharingError] = React.useState<string | null>(null);
  const previousAccounts = React.useRef(accounts);
  const monitoringQuery = useMailboxAccountMonitoringQuery({
    client,
    skip: !currentUserId || accounts.length === 0,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    context: { queryDeduplication: false },
    errorPolicy: 'none',
    returnPartialData: false,
    notifyOnNetworkStatusChange: true
  });
  const monitoringError = monitoringQuery.error
    ? messageFromError(monitoringQuery.error, 'We could not read mailbox monitoring.')
    : null;
  // Persisted state comes directly from the watched query, never a second saved-state map.
  // Do not expose previous data while a reconnect/revalidation read is outstanding or refused.
  const monitoring =
    previousAccounts.current === accounts && !monitoringQuery.loading && !monitoringError
      ? monitoringQuery.data?.mailboxAccounts
      : undefined;
  const monitoringFor = (id: string) => {
    const status = monitoring?.find((record) => record.id === id)?.monitoring;
    return completeMonitoringStatus(status) ? status : undefined;
  };
  // The loader's list is a snapshot, so the chosen mailbox is held here rather
  // than read back from it. The server allows one default per person, which is
  // what lets a single id stand for the whole list.
  const [defaultId, setDefaultId] = React.useState<string | null>(
    accounts.find((account) => account.isDefault)?.id ?? null
  );
  const [defaultError, setDefaultError] = React.useState<string | null>(null);
  const [choosingDefault, setChoosingDefault] = React.useState<string | null>(null);

  const chooseDefault = async (accountId: string) => {
    setChoosingDefault(accountId);
    setDefaultError(null);
    try {
      const result = await client.mutate<
        SetDefaultMailboxMutation,
        SetDefaultMailboxMutationVariables
      >({ mutation: SetDefaultMailboxDocument, variables: { id: accountId } });
      const updated = dataOrThrow(result)?.setDefaultMailbox;
      if (!updated) {
        throw new Error('setDefaultMailbox returned no mailbox');
      }
      setDefaultId(updated.id);
    } catch (err) {
      setDefaultError(messageFromError(err, 'We could not make this your default mailbox.'));
    } finally {
      setChoosingDefault(null);
    }
  };

  // Ownership and the access list are read here rather than through the route
  // loader, whose fragment the composer's mailbox picker shares: a picker only
  // needs an address the server already agreed it may send from.
  const hasAccounts = accounts.length > 0;
  React.useEffect(() => {
    if (!hasAccounts) return;
    let current = true;
    void client
      .query<MailboxAccountSharingQuery, MailboxAccountSharingQueryVariables>({
        query: MailboxAccountSharingDocument,
        fetchPolicy: 'network-only'
      })
      .then((result) => {
        if (!current) return;
        const data = dataOrThrow(result);
        setSharing(Object.fromEntries((data?.mailboxAccounts ?? []).map((a) => [a.id, a])));
        setSharingError(null);
      })
      .catch((err: unknown) => {
        if (!current) return;
        // Nothing read means nothing known, and a stale access list is worse
        // than an empty one: it would name senders the server never confirmed.
        setSharing({});
        setSharingError(
          messageFromError(err, 'We could not read who these mailboxes are shared with.')
        );
      });
    return () => {
      current = false;
    };
  }, [hasAccounts]);

  // Loader revalidation can follow reconnect/disconnect without changing the IDs.
  // Apollo owns this read and rejects late results from earlier refetches.
  React.useEffect(() => {
    if (previousAccounts.current === accounts) return;
    previousAccounts.current = accounts;
    if (currentUserId && accounts.length) void monitoringQuery.refetch().catch(() => {});
  }, [accounts, currentUserId, monitoringQuery.refetch]);

  return (
    <>
      {/* No overflow-hidden: each row's overflow menu is anchored, and a
          clipping ancestor is what cuts an anchored panel in half. */}
      <div className="divide-y divide-neutral-200 dark:divide-neutral-700 rounded-lg bg-white dark:bg-neutral-800 shadow">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
            Connected mailboxes
          </h3>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Cara sends email from these mailboxes, as the address on each. A mailbox you connect is
            private until it is shared, and any a colleague shared with you are listed here too —
            only the owner can reconnect a mailbox or change who may send from it.
          </p>
          {defaultError ? (
            <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">
              {defaultError}
            </p>
          ) : null}
          {sharingError ? (
            <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">
              {sharingError}
            </p>
          ) : null}
        </div>
        {accounts.length === 0 ? (
          <div className="px-4 py-5 sm:px-6">
            <MailboxEmptyState />
          </div>
        ) : (
          <ul role="list" className="divide-y divide-neutral-200 dark:divide-neutral-700">
            {accounts.map((account) => (
              <MailboxListItem
                key={account.id}
                account={account}
                sharing={sharing[account.id]}
                sharingUnavailable={!!sharingError}
                monitoring={monitoringFor(account.id)}
                onViewEvents={() => setEventsTarget(account)}
                isDefault={defaultId === account.id}
                choosingDefault={choosingDefault === account.id}
                onChooseDefault={() => void chooseDefault(account.id)}
                onDisconnect={() => setDisconnecting({ account, sharing: sharing[account.id] })}
                onManageSharing={(record) => setManaging({ account, sharing: record })}
              />
            ))}
          </ul>
        )}
      </div>

      {eventsTarget && (
        <MailboxEventsDrawer
          key={eventsTarget.id}
          account={eventsTarget}
          ownershipKnown={!!currentUserId && !!sharing[eventsTarget.id]?.owner.agencyUserId}
          includeMessages={
            !!currentUserId && sharing[eventsTarget.id]?.owner.agencyUserId === currentUserId
          }
          onClose={() => setEventsTarget(null)}
        />
      )}

      <DisconnectMailboxDialog target={disconnecting} onClose={() => setDisconnecting(null)} />

      <MailboxSharingDialog
        target={managing}
        onClose={() => setManaging(null)}
        onSaved={(updated) => {
          setSharing((current) => ({ ...current, [updated.id]: updated }));
          setManaging(null);
          revalidator.revalidate();
        }}
      />
    </>
  );
};

const MailboxListItem = ({
  account,
  sharing,
  sharingUnavailable,
  monitoring,
  onViewEvents,
  isDefault,
  choosingDefault,
  onChooseDefault,
  onDisconnect,
  onManageSharing
}: {
  account: MailboxAccountFieldsFragment;
  /** Absent until the sharing read lands, and after one that failed. */
  sharing?: MailboxSharingFieldsFragment;
  /** Whether the sharing read failed, as against not having landed yet. */
  sharingUnavailable: boolean;
  monitoring?: MailboxMonitoringFieldsFragment['monitoring'];
  onViewEvents: () => void;
  isDefault: boolean;
  choosingDefault: boolean;
  onChooseDefault: () => void;
  onDisconnect: () => void;
  onManageSharing: (sharing: MailboxSharingFieldsFragment) => void;
}) => {
  const needsReconnect = account.status === MailboxAccountStatus.NeedsReconnect;
  const provider = mailboxProviders[account.provider];
  const currentUserId = useOptionalCurrentAgencyUser()?.id;

  // Reconnect is a navigation to the provider's consent screen, which the server
  // cannot refuse, so it stays inert until the sharing read says who owns the
  // mailbox. Disconnect needs no such guard — the server refuses it.
  const ownerId = sharing?.owner.agencyUserId;
  const ownershipKnown = !!ownerId;
  const sharedWithViewer = ownershipKnown && !!currentUserId && ownerId !== currentUserId;
  const canMonitor =
    ownershipKnown && !!currentUserId && ownerId === currentUserId && provider.supportsMonitoring;
  // A monitoring-only permission problem need not prevent sending. Offer the
  // owner a reconnect without mislabelling the account's sending status.
  const monitoringNeedsReconnect =
    canMonitor && monitoring?.problemKind === MailboxMonitoringProblemKind.ReconnectRequired;

  return (
    <li className="px-4 py-5 sm:px-6">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <img src={provider.logo} alt={provider.name} className="h-8 rounded-md" />
          <div className="min-w-0">
            <h4 className="font-medium m-0 p-0 text-neutral-900 dark:text-white truncate">
              {account.identity.email}
            </h4>
            {account.identity.displayName && (
              <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">
                {account.identity.displayName}
              </p>
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {isDefault && <Badge color="violet">Default</Badge>}
          {sharedWithViewer && <Badge color="sky">Shared with you</Badge>}
          {needsReconnect ? (
            <Badge color="amber">Needs reconnect</Badge>
          ) : (
            <Badge color="green">Connected</Badge>
          )}
          {(needsReconnect || monitoringNeedsReconnect) &&
            !sharedWithViewer &&
            (ownershipKnown ? (
              <Button color="sky" href={mailboxConnectUrl(account.provider, account.id)}>
                Reconnect
              </Button>
            ) : (
              <Button color="sky" disabled>
                Reconnect
              </Button>
            ))}
          <MailboxActionsMenu
            account={account}
            sharing={sharing}
            sharedWithViewer={sharedWithViewer}
            isDefault={isDefault}
            choosingDefault={choosingDefault}
            onChooseDefault={onChooseDefault}
            onManage={() => sharing && onManageSharing(sharing)}
            onViewEvents={onViewEvents}
            onDisconnect={onDisconnect}
          />
        </div>
      </div>
      {canMonitor && monitoring && (
        <p className="mt-3 text-sm text-neutral-500 dark:text-neutral-400">
          {mailboxMonitoringLabel(monitoring)}
          {monitoring.problem ? `: ${monitoring.problem}` : ''}
        </p>
      )}
      {/* Keep monitoring health on the account tile, independent of manual controls. */}
      {canMonitor &&
        monitoring?.notes?.some((note) => note.code === MailboxMonitoringNoteCode.Unknown) && (
          <p role="status" className="mt-2 text-sm text-amber-700 dark:text-amber-300">
            Some requested folders could not be watched.
          </p>
        )}
      {needsReconnect && !sharedWithViewer && (
        <p className="mt-4 text-sm text-neutral-500 dark:text-neutral-400">
          The sign-in for this mailbox expired or was revoked. Cara cannot read or send from it
          until {ownershipKnown ? 'you reconnect' : 'its owner reconnects'}.
          {sharingUnavailable
            ? ' We could not check whether that is you, so reconnecting is unavailable until this page is reloaded.'
            : ''}
        </p>
      )}
      <MailboxAccessSection
        sharing={sharing}
        sharingUnavailable={sharingUnavailable}
        sharedWithViewer={sharedWithViewer}
        needsReconnect={needsReconnect}
      />
    </li>
  );
};

/**
 * A DropdownLabel here would adopt the enclosing Field's htmlFor and
 * preventDefault the click, so every item is a DropdownItem (a real button).
 */
const MailboxActionsMenu = ({
  account,
  sharing,
  sharedWithViewer,
  isDefault,
  choosingDefault,
  onChooseDefault,
  onManage,
  onViewEvents,
  onDisconnect
}: {
  account: MailboxAccountFieldsFragment;
  sharing?: MailboxSharingFieldsFragment;
  sharedWithViewer: boolean;
  isDefault: boolean;
  choosingDefault: boolean;
  onChooseDefault: () => void;
  onManage: () => void;
  onViewEvents: () => void;
  onDisconnect: () => void;
}) => {
  const canShare = useVisibilityEntitlement();

  if (sharedWithViewer) {
    return null;
  }

  const sharedWithAnyone =
    sharing?.visibility.level === Visibility.Public ||
    (sharing?.visibility.userIds.length ?? 0) + (sharing?.visibility.teamIds.length ?? 0) > 0;

  return (
    <Dropdown>
      <DropdownButton plain aria-label={`Actions for ${account.identity.email}`}>
        <EllipsisVerticalIcon className="size-5" />
      </DropdownButton>
      <DropdownMenu anchor="bottom end">
        {!isDefault && (
          <DropdownItem onClick={onChooseDefault} disabled={choosingDefault}>
            <StarIcon data-slot="icon" />
            Make default
          </DropdownItem>
        )}
        {canShare && sharing && (
          <DropdownItem onClick={onManage}>
            <UsersIcon data-slot="icon" />
            {sharedWithAnyone ? 'Manage access' : 'Share mailbox'}
          </DropdownItem>
        )}
        <DropdownItem onClick={onViewEvents}>
          <ClockIcon data-slot="icon" />
          Mailbox events
        </DropdownItem>
        <DropdownItem onClick={onDisconnect} className={destructiveDropdownItemClass}>
          <PowerIcon data-slot="icon" />
          Disconnect
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
};

type MonitoringStatus = NonNullable<MailboxMonitoringFieldsFragment['monitoring']>;

export const mailboxMonitoringLabel = (status: MonitoringStatus): string => {
  if (status.problemKind || status.problem) return 'Monitoring needs attention';
  if (!status.enabled) return status.active ? 'Stopping monitoring' : 'Monitoring off';
  if (status.recovering) return 'Rechecking drafts';
  return status.active ? 'Monitoring on' : 'Monitoring pending';
};

const completeMonitoringStatus = (
  status: MonitoringStatus | null | undefined
): status is MonitoringStatus =>
  !!status &&
  ['enabled', 'active', 'recovering', 'processUpdates', 'processDeletes'].every(
    (key) => typeof status[key as keyof MonitoringStatus] === 'boolean'
  ) &&
  typeof status.problem === 'string' &&
  (status.expiresAt === null ||
    status.expiresAt instanceof Date ||
    typeof status.expiresAt === 'string') &&
  (status.problemKind === null || typeof status.problemKind === 'string') &&
  Array.isArray(status.folders) &&
  status.folders.every(
    (folder) =>
      !!folder &&
      typeof folder.id === 'string' &&
      typeof folder.name === 'string' &&
      typeof folder.missing === 'boolean'
  );

/**
 * The kit concatenates the caller's classes first, so plain colour utilities
 * would be settled by stylesheet order rather than intent.
 */
const destructiveDropdownItemClass = clsx(
  '!text-red-600 dark:!text-red-400 [&>[data-slot=icon]]:!text-red-600 dark:[&>[data-slot=icon]]:!text-red-400',
  'data-[focus]:!bg-red-600 data-[focus]:!text-white',
  '[&>[data-slot=icon]]:data-[focus]:!text-white'
);

const MailboxAccessSection = ({
  sharing,
  sharingUnavailable,
  sharedWithViewer,
  needsReconnect
}: {
  sharing?: MailboxSharingFieldsFragment;
  sharingUnavailable: boolean;
  sharedWithViewer: boolean;
  needsReconnect: boolean;
}) => {
  const canShare = useVisibilityEntitlement();
  const userIds = sharing?.visibility.userIds ?? [];
  const teamIds = sharing?.visibility.teamIds ?? [];
  const agencyWide = sharing?.visibility.level === Visibility.Public;
  const { data: usersData } = useGetAgencyUsersQuery({ fetchPolicy: 'cache-first' });
  const { data: teamsData } = useGetTeamsQuery({
    fetchPolicy: 'cache-first',
    skip: teamIds.length === 0
  });

  const userNames = new Map(
    (usersData?.agencyUsers ?? []).map((user) => [user.id, user.name || user.email])
  );
  const teamNames = new Map((teamsData?.teams ?? []).map((team) => [team.id, team.name]));

  if (sharing && sharedWithViewer) {
    // 'a colleague' keeps every sentence below parsing when the directory has not loaded.
    const owner = userNames.get(sharing.owner.agencyUserId) ?? 'a colleague';
    return (
      <div className={accessSectionClass}>
        <p className="text-sm font-medium text-neutral-900 dark:text-white">
          Shared with you by {owner}
        </p>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          {needsReconnect ? (
            <>
              The sign-in behind this mailbox has lapsed, so nobody can send from it until {owner}{' '}
              reconnects it. Only the owner of a mailbox can do that.
            </>
          ) : (
            <>
              You can send email that arrives from this address. It goes out through {owner}&rsquo;s
              own sign-in with the mail provider, so only they can reconnect this mailbox or change
              who else may send from it — and if that sign-in lapses, sending stops for everyone it
              is shared with at once.
            </>
          )}
        </p>
      </div>
    );
  }

  const senders = [
    ...userIds.map((id) => ({
      id,
      kind: 'user' as const,
      label: userNames.get(id) ?? unknownId(id)
    })),
    ...teamIds.map((id) => ({
      id,
      kind: 'team' as const,
      label: teamNames.get(id) ?? unknownId(id)
    }))
  ];
  const sharedWithAnyone = agencyWide || senders.length > 0;

  return (
    <div className={accessSectionClass}>
      <p className="text-sm font-medium text-neutral-900 dark:text-white">
        Who can send as this mailbox
      </p>
      {/* An empty box under this heading would read as "nobody", which is a
          claim about the mailbox. A failed read is a claim about Cara. */}
      {sharingUnavailable && (
        <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
          We could not read this. Reload the page to see who can send as this mailbox.
        </p>
      )}
      {sharing && (
        <>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <SenderChip icon={UserIcon} label="You" />
            {agencyWide ? (
              <SenderChip icon={UserGroupIcon} label="Everyone at your agency" broad />
            ) : (
              senders.map((sender) => (
                <SenderChip
                  key={`${sender.kind}-${sender.id}`}
                  icon={sender.kind === 'team' ? UserGroupIcon : UserIcon}
                  label={sender.kind === 'team' ? `${sender.label} (team)` : sender.label}
                />
              ))
            )}
          </div>
          <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
            {agencyWide ? (
              <>
                Everyone at your agency, and anyone who joins later, can send email that arrives
                from this address, under your name. The send log records who actually pressed send.
              </>
            ) : senders.length === 0 ? (
              canShare ? (
                <>
                  Nobody else. Share it and a colleague can send email that arrives from this
                  address, under your name.
                </>
              ) : (
                <>
                  Nobody else. Sharing a mailbox with a colleague requires a Pro or Enterprise plan.
                </>
              )
            ) : (
              <>
                They send email that arrives from this address, under your name. The send log
                records who actually pressed send.
              </>
            )}
          </p>
          {needsReconnect && sharedWithAnyone && (
            <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
              None of them can send until you reconnect: the mailbox runs on your sign-in, and
              nobody else can repair it.
            </p>
          )}
        </>
      )}
    </div>
  );
};

const accessSectionClass = 'mt-4 rounded-md border border-neutral-200 dark:border-neutral-700 p-3';

/** An ID the directory does not know, shown rather than dropped. */
const unknownId = (id: string) => `Unknown (${id})`;

/** `broad` marks a grant that keeps widening as people are hired. */
const SenderChip = ({
  icon: Icon,
  label,
  broad = false
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  broad?: boolean;
}) => (
  <span
    className={clsx(
      'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
      broad
        ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300 ring-1 ring-inset ring-amber-200 dark:ring-amber-500/30'
        : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300'
    )}
  >
    <Icon className="size-3.5" aria-hidden="true" />
    {label}
  </span>
);

/**
 * PUBLIC is offered even where the agency disallows private work product: that
 * policy governs work product, and an unshared mailbox is not withheld work.
 */
const MailboxSharingDialog = ({
  target,
  onClose,
  onSaved
}: {
  target: MailboxSharingTarget | null;
  onClose: () => void;
  onSaved: (sharing: MailboxSharingFieldsFragment) => void;
}) => {
  const [draft, setDraft] = React.useState<VisibilitySelectorValue>(privateVisibility);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  // Opening the dialog is what seeds the draft, so a second visit starts from
  // what the server last confirmed rather than from an abandoned edit.
  React.useEffect(() => {
    if (!target) return;
    setDraft(visibilityValueFromFragment(target.sharing.visibility));
    setSaveError(null);
  }, [target]);

  const save = async () => {
    if (!target) return;
    setSaving(true);
    setSaveError(null);
    try {
      const result = await client.mutate<
        SetMailboxVisibilityMutation,
        SetMailboxVisibilityMutationVariables
      >({
        mutation: SetMailboxVisibilityDocument,
        variables: {
          id: target.account.id,
          visibility: visibilityInputFromValue(withoutEmptyAudience(draft))
        }
      });
      const updated = dataOrThrow(result)?.setMailboxVisibility;
      // A mutation that neither refused nor returned the mailbox has not saved
      // anything, and closing the editor on it would claim otherwise.
      if (!updated) {
        throw new Error('setMailboxVisibility returned no mailbox');
      }
      onSaved(updated);
    } catch (err) {
      setSaveError(messageFromError(err, 'We could not change who can send as this mailbox.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!target} onClose={onClose}>
      <DialogTitle>Who can send as this mailbox</DialogTitle>
      <DialogDescription>
        {target ? (
          <>
            Everyone you name here can send email that arrives from{' '}
            <strong>{target.account.identity.email}</strong>, under your name. Cara&rsquo;s send log
            records which of you pressed send.
          </>
        ) : null}
      </DialogDescription>
      <DialogBody>
        {/* You already hold this mailbox — the server admits its owner whatever
            the access list says — so naming yourself could only fail. */}
        <VisibilitySelector
          value={draft}
          onChange={setDraft}
          excludeUserIds={target ? [target.sharing.owner.agencyUserId] : []}
        />
        {draft.level === Visibility.Public && (
          <p className="mt-3 rounded-md border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-200">
            Everyone at your agency can send email that arrives from this address, and anyone who
            joins later inherits that access without you sharing it again.
          </p>
        )}
        {draft.level === Visibility.Shared &&
          draft.userIds.length === 0 &&
          draft.teamIds.length === 0 && (
            <p className="mt-3 text-sm text-neutral-500 dark:text-neutral-400">
              Nobody is named yet. Saving now keeps this mailbox private.
            </p>
          )}
        <p className="mt-4 rounded-md border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-200">
          A shared mailbox still runs on your sign-in with the mail provider. If it lapses — a
          password change, a withdrawn approval, or your Cara account being closed — sending stops
          for everyone at once, and you are the only one who can reconnect it.
        </p>
        {saveError && <ErrorMessage className="mt-4">{saveError}</ErrorMessage>}
      </DialogBody>
      <DialogActions>
        <Button outline onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button color="sky" loading={saving} onClick={() => void save()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Shared-with-nobody is private; storing it as shared would claim an empty audience. */
export const withoutEmptyAudience = (value: VisibilitySelectorValue): VisibilitySelectorValue =>
  value.level === Visibility.Shared && value.userIds.length === 0 && value.teamIds.length === 0
    ? { ...value, level: Visibility.Private }
    : value;

/**
 * visibilityValueFromFragment defaults to PUBLIC, a level to arrive at
 * deliberately rather than start from.
 */
const privateVisibility: VisibilitySelectorValue = {
  level: Visibility.Private,
  userIds: [],
  teamIds: []
};

/**
 * `connectable` is the providers the server holds credentials for, which is
 * narrower than what the schema can name.
 */
const ConnectMailboxCard = ({ connectable }: { connectable: MailboxProvider[] }) => {
  const providers = mailboxProviderList.filter((provider) => connectable.includes(provider));

  return (
    <div className="divide-y divide-neutral-200 dark:divide-neutral-700 overflow-hidden rounded-lg bg-white dark:bg-neutral-800 shadow">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
          Available providers
        </h3>
      </div>
      {providers.length === 0 ? (
        <ConnectProvidersEmptyState />
      ) : (
        /* The 1px gaps show the neutral backing as hairlines between cells, so a
           column with nothing in it reads as a grey slab. Widen only once there
           are cells to fill the row. */
        <div
          className={clsx(
            'grid grid-cols-1 gap-[1px] bg-neutral-200 dark:bg-neutral-700',
            providers.length >= 2 && 'sm:grid-cols-2',
            providers.length >= 3 && 'xl:grid-cols-3'
          )}
        >
          {providers.map((provider) => (
            <ConnectProviderCell key={provider} provider={provider} />
          ))}
        </div>
      )}
    </div>
  );
};

const ConnectProvidersEmptyState = () => (
  <div className="text-center py-10">
    <EnvelopeIcon className="mx-auto size-10 text-neutral-400 dark:text-neutral-500" />
    <h4 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
      No providers available
    </h4>
    <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400 sm:max-w-md mx-auto">
      Cara has no mail provider to connect to here yet. Once one is available it will show up in
      this list.
    </p>
  </div>
);

const ConnectProviderCell = ({ provider }: { provider: MailboxProvider }) => {
  const { name, logo, capabilities } = mailboxProviders[provider];

  return (
    <div className="px-4 py-5 sm:px-6 bg-white dark:bg-neutral-800">
      <div className="flex items-center gap-3">
        <img src={logo} alt="" className="h-8 rounded-md" />
        <h4 className="font-medium m-0 p-0 text-neutral-900 dark:text-white">{name}</h4>
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        {capabilities.map((capability) => (
          <Badge key={capability} color="neutral">
            {capability}
          </Badge>
        ))}
      </div>
      <div className="mt-4">
        <Button color="sky" className="w-full" href={mailboxConnectUrl(provider)}>
          Connect {name}
        </Button>
      </div>
    </div>
  );
};

const DisconnectMailboxDialog = ({
  target,
  onClose
}: {
  target: MailboxDisconnectTarget | null;
  onClose: () => void;
}) => {
  const account = target?.account ?? null;
  const fetcher = useFetcher();
  const submitting = fetcher.state !== 'idle';
  const result = fetcher.data as ActionResult<unknown>;
  const failure = result?.applicationError?.message ?? result?.serverError?.message;

  // The dialog closes on the submission settling rather than on the click, so a
  // slow revoke keeps the confirmation on screen instead of dropping the user
  // back onto a list that has not changed yet. A refusal keeps it open, since
  // the message has nowhere else to go once the dialog is gone.
  React.useEffect(() => {
    if (fetcher.state === 'idle' && result && !failure) {
      onClose();
    }
  }, [fetcher.state, result, failure, onClose]);

  return (
    <Dialog open={!!account} onClose={onClose}>
      <DialogTitle>Disconnect mailbox</DialogTitle>
      <DialogDescription>
        {account ? (
          <>
            Cara will forget the sign-in for <strong>{account.identity.email}</strong> and stop
            sending from it{disconnectAudience(target?.sharing)}. Email already sent stays in your
            send log. You can connect the mailbox again at any time, and whoever you shared it with
            keeps their access.
          </>
        ) : null}
      </DialogDescription>
      {failure ? (
        <DialogBody>
          <ErrorMessage>{failure}</ErrorMessage>
        </DialogBody>
      ) : null}
      <DialogActions>
        <Button outline onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          color="red"
          loading={submitting}
          onClick={() => {
            if (!account) return;
            fetcher.submit(
              { id: account.id },
              { method: 'POST', action: 'disconnect', encType: 'application/json' }
            );
          }}
        >
          Disconnect
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Empty when nothing is shared or the record has not loaded, so the
 *  surrounding sentence reads either way. */
const disconnectAudience = (sharing?: MailboxSharingFieldsFragment): string => {
  if (!sharing) return '';
  if (sharing.visibility.level === Visibility.Public) {
    return ', for everyone at your agency at once';
  }
  const named = sharing.visibility.userIds.length + sharing.visibility.teamIds.length;
  if (named === 0) return '';
  return `, for you and the ${named} ${named === 1 ? 'person or team' : 'people and teams'} you shared it with`;
};

const MailboxEmptyState = () => (
  <div className="text-center py-10">
    <EnvelopeIcon className="mx-auto size-10 text-neutral-400 dark:text-neutral-500" />
    <h4 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
      No mailboxes connected
    </h4>
    <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400 sm:max-w-md mx-auto">
      Connect a mailbox above so Cara can send the emails it drafts from your own address.
    </p>
  </div>
);

const MailboxPageLoader = () => (
  <div className="rounded-lg bg-white dark:bg-neutral-800 shadow px-6">
    <div className="text-center py-12">
      <Spinner size="size-8" color="neutral" />
      <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
        Loading mailboxes...
      </h3>
      <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
        This might take a moment.
      </p>
    </div>
  </div>
);

type SignatureDraft = {
  id?: string;
  name: string;
  bodyHtml: string;
  isDefault: boolean;
  /** Absent on a new signature, which the editor starts private. */
  visibility?: VisibilitySelectorValue;
};

/**
 * Mail clients drop <style> blocks and much of the box model, so the seed is a
 * table with every rule inline.
 */
const newSignatureHtml = `<table style="border-collapse:collapse;font-family:Arial,Helvetica,sans-serif;font-size:13px;color:#1f2937"><tr>
<td style="vertical-align:top;border-left:3px solid #2563eb;padding-left:12px">
<div style="font-weight:bold;font-size:14px">Your name</div>
<div style="color:#6b7280">Your title &middot; Your agency</div>
<div style="margin-top:6px"><a href="tel:+15550100" style="color:#2563eb;text-decoration:none">(555) 010-0100</a></div>
</td></tr></table>`;

const imgTagPattern = /<img\b[^>]*>/gi;
const styleAttributePattern = /\sstyle\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'>]+)/i;

const attributeValue = (tag: string, name: string): string | null => {
  const match = new RegExp(`\\s${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s"'>]+))`, 'i').exec(
    tag
  );
  return match ? (match[1] ?? match[2] ?? match[3] ?? null) : null;
};

/**
 * Outlook strips width/height attributes when sanitizing incoming mail, so an
 * image sized only by attribute renders at its natural size — double, for a 2x
 * asset. Inline CSS survives, so the dimension is stated in both places.
 */
export const withInlineImageDimensions = (html: string): string =>
  html.replace(imgTagPattern, (tag) => {
    const style = attributeValue(tag, 'style') ?? '';
    const added = (['width', 'height'] as const).flatMap((dimension) => {
      const value = attributeValue(tag, dimension);
      // Only a bare number means pixels. A percentage or a calc() is already
      // CSS and belongs to whoever wrote it.
      if (!value || !/^\d+$/.test(value)) return [];
      if (new RegExp(`(^|;)\\s*${dimension}\\s*:`, 'i').test(style)) return [];
      return [`${dimension}:${value}px`];
    });
    if (added.length === 0) {
      return tag;
    }

    const declarations = [style.trim().replace(/;$/, ''), ...added].filter(Boolean).join(';');
    // A style value carrying both quote characters cannot be re-emitted as an
    // attribute without escaping, and mangling a signature is worse than
    // leaving one image unsized.
    if (declarations.includes('"') && declarations.includes("'")) {
      return tag;
    }
    const quote = declarations.includes('"') ? "'" : '"';
    const attribute = ` style=${quote}${declarations}${quote}`;
    return style
      ? tag.replace(styleAttributePattern, attribute)
      : tag.replace(/<img\b/i, `<img${attribute}`);
  });

const MailboxSignaturesCard = () => {
  const currentUserId = useOptionalCurrentAgencyUser()?.id;
  const [signatures, setSignatures] = React.useState<MailboxSignatureSettingsFieldsFragment[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [draft, setDraft] = React.useState<SignatureDraft | null>(null);
  const [deleting, setDeleting] = React.useState<MailboxSignatureSettingsFieldsFragment | null>(
    null
  );

  // The one-default rule is enforced in a transaction that also clears the
  // previous default, so the server's list is the only place the flags are all
  // consistent.
  const load = React.useCallback(
    () =>
      client
        .query<MailboxSignaturesForSettingsQuery, MailboxSignaturesForSettingsQueryVariables>({
          query: MailboxSignaturesForSettingsDocument,
          fetchPolicy: 'network-only'
        })
        .then((result) => {
          setSignatures(dataOrThrow(result)?.mailboxSignatures ?? []);
          setLoadError(null);
        })
        .catch((err: unknown) => {
          setSignatures([]);
          setLoadError(messageFromError(err, 'We could not load your signatures.'));
        })
        .finally(() => setLoading(false)),
    []
  );

  React.useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <div className="divide-y divide-neutral-200 dark:divide-neutral-700 overflow-hidden rounded-lg bg-white dark:bg-neutral-800 shadow">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <h3 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
                Email signatures
              </h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                Email signatures live in Cara, so the same one works from every mailbox you connect.
                The composer appends the one you pick to the email it sends. These are separate from
                the producer signature that fills in on generated forms.
              </p>
            </div>
            <div className="shrink-0">
              <Button
                color="sky"
                onClick={() => setDraft({ name: '', bodyHtml: newSignatureHtml, isDefault: false })}
              >
                New email signature
              </Button>
            </div>
          </div>
        </div>
        <div className="px-4 py-5 sm:px-6">
          {loadError ? (
            // ErrorMessage is a Headless description and needs a field or a
            // dialog above it; this failure belongs to the card, not a control.
            <p role="alert" className="text-sm text-red-600 dark:text-red-400">
              {loadError}
            </p>
          ) : loading ? (
            <div className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
              <Spinner size="size-4" color="neutral" />
              Loading signatures...
            </div>
          ) : signatures.length === 0 ? (
            <SignatureEmptyState />
          ) : (
            <ul role="list" className="divide-y divide-neutral-200 dark:divide-neutral-700">
              {signatures.map((signature) => (
                <SignatureRow
                  key={signature.id}
                  signature={signature}
                  // Ownership is the server's call, so the button is hidden only
                  // where the client can tell: outside the dashboard tree there
                  // is no signed-in user to compare against.
                  editable={!currentUserId || signature.owner.agencyUserId === currentUserId}
                  onDelete={() => setDeleting(signature)}
                  onEdit={() =>
                    setDraft({
                      id: signature.id,
                      name: signature.name,
                      bodyHtml: signature.bodyHtml,
                      isDefault: signature.isDefault,
                      visibility: visibilityValueFromFragment(signature.visibility)
                    })
                  }
                />
              ))}
            </ul>
          )}
        </div>
      </div>

      <SignatureDialog
        draft={draft}
        onClose={() => setDraft(null)}
        onSaved={() => {
          setDraft(null);
          void load();
        }}
      />

      <DeleteSignatureDialog
        signature={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={() => {
          setDeleting(null);
          void load();
        }}
      />
    </>
  );
};

const SignatureRow = ({
  signature,
  editable,
  onEdit,
  onDelete
}: {
  signature: MailboxSignatureSettingsFieldsFragment;
  editable: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) => (
  <li className="py-4 first:pt-0 last:pb-0">
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
            {signature.name}
          </p>
          {signature.isDefault && <Badge color="violet">Default</Badge>}
        </div>
        {!editable && (
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            Shared with you by a colleague. You can pick it when composing, but only its author can
            change it.
          </p>
        )}
      </div>
      {editable && (
        <div className="flex shrink-0 items-center gap-2">
          <Button outline onClick={onEdit}>
            Edit
          </Button>
          <Button plain onClick={onDelete}>
            Delete
          </Button>
        </div>
      )}
    </div>
    {/* Unframed: the row already encloses it, and the audience panel below is
        the one bordered block, matching the mailbox rows above. */}
    <SignaturePreview bodyHtml={signature.bodyHtml} bordered={false} className="mt-3" />
    {editable && <SignatureAudienceSection visibility={signature.visibility} />}
  </li>
);

const SignatureAudienceSection = ({
  visibility
}: {
  visibility: MailboxSignatureSettingsFieldsFragment['visibility'];
}) => {
  const canShare = useVisibilityEntitlement();
  const agencyWide = visibility.level === Visibility.Public;
  const { data: usersData } = useGetAgencyUsersQuery({ fetchPolicy: 'cache-first' });
  const { data: teamsData } = useGetTeamsQuery({
    fetchPolicy: 'cache-first',
    skip: visibility.teamIds.length === 0
  });

  const userNames = new Map(
    (usersData?.agencyUsers ?? []).map((user) => [user.id, user.name || user.email])
  );
  const teamNames = new Map((teamsData?.teams ?? []).map((team) => [team.id, team.name]));

  const holders = [
    ...visibility.userIds.map((id) => ({
      id,
      kind: 'user' as const,
      label: userNames.get(id) ?? unknownId(id)
    })),
    ...visibility.teamIds.map((id) => ({
      id,
      kind: 'team' as const,
      label: teamNames.get(id) ?? unknownId(id)
    }))
  ];

  return (
    <div className={accessSectionClass}>
      <p className="text-sm font-medium text-neutral-900 dark:text-white">
        Who can pick this signature
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <SenderChip icon={UserIcon} label="You" />
        {agencyWide ? (
          <SenderChip icon={UserGroupIcon} label="Everyone at your agency" broad />
        ) : (
          holders.map((holder) => (
            <SenderChip
              key={`${holder.kind}-${holder.id}`}
              icon={holder.kind === 'team' ? UserGroupIcon : UserIcon}
              label={holder.kind === 'team' ? `${holder.label} (team)` : holder.label}
            />
          ))
        )}
      </div>
      <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
        {agencyWide ? (
          <>
            Everyone at your agency, and anyone who joins later, can pick this signature when they
            compose. It goes out under their own name, from their own mailbox.
          </>
        ) : holders.length === 0 ? (
          canShare ? (
            <>Nobody else. Share it and a colleague can pick it when they compose.</>
          ) : (
            <>
              Nobody else. Sharing a signature with a colleague requires a Pro or Enterprise plan.
            </>
          )
        ) : (
          <>They can pick this signature when they compose, and you stay its only author.</>
        )}
      </p>
    </div>
  );
};

const DeleteSignatureDialog = ({
  signature,
  onClose,
  onDeleted
}: {
  signature: MailboxSignatureSettingsFieldsFragment | null;
  onClose: () => void;
  onDeleted: () => void;
}) => {
  const [deleting, setDeleting] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (signature) {
      setDeleteError(null);
    }
  }, [signature]);

  const confirm = async () => {
    if (!signature) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const result = await client.mutate<
        DeleteMailboxSignatureMutation,
        DeleteMailboxSignatureMutationVariables
      >({
        mutation: DeleteMailboxSignatureDocument,
        variables: { id: signature.id }
      });
      dataOrThrow(result);
      onDeleted();
    } catch (err) {
      setDeleteError(messageFromError(err, 'We could not delete this email signature.'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={!!signature} onClose={onClose}>
      <DialogTitle>Delete email signature</DialogTitle>
      <DialogDescription>
        {signature ? (
          <>
            <strong>{signature.name}</strong> will stop appearing in the composer, and colleagues
            you shared it with will lose it too. Email you have already sent is unaffected; a draft
            still set to use it needs another signature before it can be sent.
          </>
        ) : null}
      </DialogDescription>
      {deleteError ? (
        <DialogBody>
          <ErrorMessage>{deleteError}</ErrorMessage>
        </DialogBody>
      ) : null}
      <DialogActions>
        <Button outline onClick={onClose} disabled={deleting}>
          Cancel
        </Button>
        <Button color="red" loading={deleting} onClick={() => void confirm()}>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SignatureEmptyState = () => (
  <div className="text-center py-10">
    <PencilSquareIcon className="mx-auto size-10 text-neutral-400 dark:text-neutral-500" />
    <h4 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
      No signatures yet
    </h4>
    <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400 sm:max-w-md mx-auto">
      Create one and Cara can sign the emails it drafts for you the way you sign your own.
    </p>
  </div>
);

const SignatureDialog = ({
  draft,
  onClose,
  onSaved
}: {
  draft: SignatureDraft | null;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const [name, setName] = React.useState('');
  const [isDefault, setIsDefault] = React.useState(false);
  const [visibility, setVisibility] = React.useState<VisibilitySelectorValue>(privateVisibility);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [editorKey, setEditorKey] = React.useState(0);
  const canShare = useVisibilityEntitlement();
  const authorId = useOptionalCurrentAgencyUser()?.id;

  // The editor debounces, so the last keystroke before Save can still be in
  // flight; the ref holds what it has emitted.
  const bodyHtmlRef = React.useRef('');

  // The editor reads its content once, when it is built, so switching
  // signatures has to remount it.
  React.useEffect(() => {
    if (!draft) return;
    setName(draft.name);
    bodyHtmlRef.current = draft.bodyHtml;
    setIsDefault(draft.isDefault);
    setVisibility(draft.visibility ?? privateVisibility);
    setSaveError(null);
    setEditorKey((key) => key + 1);
  }, [draft]);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setSaveError(null);
    try {
      const result = await client.mutate<
        UpsertMailboxSignatureMutation,
        UpsertMailboxSignatureMutationVariables
      >({
        mutation: UpsertMailboxSignatureDocument,
        variables: {
          input: {
            id: draft.id,
            name: name.trim(),
            bodyHtml: withInlineImageDimensions(bodyHtmlRef.current),
            isDefault,
            // The upsert writes the whole row, so the sharing goes with every
            // save — an omitted one would quietly make the signature private
            // again, whether or not this editor touched it.
            visibility: visibilityInputFromValue(withoutEmptyAudience(visibility))
          }
        }
      });
      dataOrThrow(result);
      onSaved();
    } catch (err) {
      setSaveError(messageFromError(err, 'We could not save this signature.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog size="3xl" open={!!draft} onClose={onClose}>
      <DialogTitle>{draft?.id ? 'Edit signature' : 'New signature'}</DialogTitle>
      <DialogDescription>
        Write the signature as it should arrive. The composer appends it to the email it sends.
      </DialogDescription>
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Name</Label>
              <Input
                name="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Client emails"
              />
              <Description>
                Only you see this name. It is what the composer's signature list shows.
              </Description>
            </Field>
            <Field>
              <Label>Email signature</Label>
              <div className="signature-editor flex h-72 flex-col">
                <HtmlDocumentEditor
                  key={editorKey}
                  showImageButton={false}
                  content={draft?.bodyHtml ?? ''}
                  editable
                  onUpdate={(content) => {
                    bodyHtmlRef.current = content;
                  }}
                />
              </div>
              <Description>
                Images have to live at a public URL a recipient's mail client can fetch, so a logo
                comes in by pasting a signature that already links to one rather than by uploading a
                file here.
              </Description>
            </Field>
            <SwitchField>
              <Label>Use as my default</Label>
              <Description>
                Preselected when Cara drafts you an email. You have one default at most, so turning
                this on takes it off whichever signature holds it now, and emails already composed
                keep the signature they were given.
              </Description>
              <Switch
                name="isDefault"
                color="sky"
                checked={isDefault}
                onChange={setIsDefault}
                aria-label="Use as my default"
              />
            </SwitchField>
          </FieldGroup>
        </Fieldset>
        {/* Outside the Fieldset on purpose: a Headless Label inside a Field
            claims the field's htmlFor, and the radios below would inherit a
            label belonging to another control. */}
        <div className="mt-6 border-t border-neutral-200 dark:border-neutral-700 pt-6">
          <p className="text-sm font-medium text-neutral-900 dark:text-white">
            Who can pick this signature
          </p>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            A signature is yours alone until you share it. Whoever you name here can pick it in the
            composer; it still goes out under their own name, from their own mailbox, and you stay
            its only author.
          </p>
          {canShare ? (
            <div className="mt-3">
              <VisibilitySelector
                value={visibility}
                onChange={setVisibility}
                excludeUserIds={authorId ? [authorId] : []}
              />
              {visibility.level === Visibility.Public && (
                <p className="mt-3 rounded-md border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-200">
                  Everyone at your agency can pick this signature, and anyone who joins later
                  inherits it without you sharing it again.
                </p>
              )}
              {visibility.level === Visibility.Shared &&
                visibility.userIds.length === 0 &&
                visibility.teamIds.length === 0 && (
                  <p className="mt-3 text-sm text-neutral-500 dark:text-neutral-400">
                    Nobody is named yet. Saving now keeps this signature private.
                  </p>
                )}
            </div>
          ) : (
            <p className="mt-3 text-sm text-neutral-500 dark:text-neutral-400">
              This signature stays private to you. Sharing a signature with a colleague requires a
              Pro or Enterprise plan.
            </p>
          )}
        </div>
        {saveError && <ErrorMessage className="mt-4">{saveError}</ErrorMessage>}
      </DialogBody>
      <DialogActions>
        <Button outline onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button color="sky" loading={saving} disabled={!name.trim()} onClick={() => void save()}>
          Save signature
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type MailboxOutcome = { tone: 'success' | 'warning' | 'error'; title: string; body: string };

/**
 * The query parameters the OAuth callback lands on. They are a contract with
 * pkg/service/api/http/mailbox_oauth.go, which redirects here with the outcome.
 */
const mailboxOutcomeParams = [
  'mailbox_status',
  'mailbox_error',
  'mailbox_message',
  'mailbox_account_id',
  'mailbox_tenant'
];

/**
 * The server's own message wins wherever it supplies one: it knows why a
 * provider refused or which permission was missing.
 */
export const describeMailboxOutcome = (params: URLSearchParams): MailboxOutcome | null => {
  const message = params.get('mailbox_message');
  const tenant = params.get('mailbox_tenant');

  switch (params.get('mailbox_status')) {
    case 'connected':
      return {
        tone: 'success',
        title: 'Mailbox connected',
        body: message ?? 'Cara can now send email from this mailbox.'
      };
    case 'reconnected':
      return {
        tone: 'success',
        title: 'Mailbox reconnected',
        body: message ?? 'The sign-in was renewed. Cara can send from this mailbox again.'
      };
    // Already-connected is the state the user asked for, so: success.
    case 'already_connected':
      return {
        tone: 'success',
        title: 'Mailbox already connected',
        body: message ?? 'That mailbox is already connected. Nothing changed.'
      };
    case 'admin_consent_granted':
      return {
        tone: 'success',
        title: 'Approval granted',
        body: tenant
          ? `Cara is approved for ${tenant}. Work and school mailboxes in that organization can now be connected.`
          : 'Cara is approved for your organization. Work and school mailboxes can now be connected.'
      };
    case 'admin_consent_denied':
      return {
        tone: 'warning',
        title: 'Approval not granted',
        body:
          message ??
          'Approval was not granted, so work and school mailboxes in this organization cannot be connected yet.'
      };
    case 'error':
      return {
        tone: 'error',
        title: 'Mailbox not connected',
        body: message ?? 'We could not connect that mailbox. Try again.'
      };
    // Anything outside the contract above arrived on a hand-edited URL.
    default:
      return null;
  }
};

const outcomeToneStyles: Record<MailboxOutcome['tone'], string> = {
  success:
    'border-green-200 dark:border-green-500/30 bg-green-50 dark:bg-green-500/10 text-green-900 dark:text-green-200',
  warning:
    'border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 text-amber-900 dark:text-amber-200',
  error:
    'border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 text-red-900 dark:text-red-200'
};

export const MailboxOutcomeBanner = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const outcome = describeMailboxOutcome(searchParams);

  if (!outcome) {
    return null;
  }

  const dismiss = () => {
    const remaining = new URLSearchParams(searchParams);
    mailboxOutcomeParams.forEach((param) => remaining.delete(param));
    setSearchParams(remaining, { replace: true });
  };

  return (
    <div
      role="status"
      className={clsx(
        'flex items-start gap-3 rounded-lg border p-4',
        outcomeToneStyles[outcome.tone]
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{outcome.title}</p>
        <p className="mt-1 text-sm">{outcome.body}</p>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="shrink-0 rounded-md p-1 hover:bg-black/5 dark:hover:bg-white/10"
      >
        <XMarkIcon className="size-4" />
      </button>
    </div>
  );
};
